import { useEffect } from 'react'
import { type Moment } from 'moment'
import { trackEvent } from 'utils/events/trackEvent'
import { useRetailerId } from 'utils/routing/params.hooks'
import { TRACKING } from 'domains/aggregate-store-performance/utils/constants/AggregateStorePerformanceOverviewPage.constants'
import {
  getEndOfWeek,
  momentToQueryStringDate,
} from 'domains/aggregate-store-performance/utils/helpers/AggregateStorePerformanceOverviewPage.helpers'
import { type FiltersDropdownOption } from 'domains/aggregate-store-performance/components/filters-dropdown/utils/FiltersDropdown.types'

interface useTrackSortingProps {
  columnId: string
  isSortedDesc: boolean
  retailerId: string
}

const previousSorting: Omit<useTrackSortingProps, 'retailerId'> = {
  columnId: '',
  isSortedDesc: false,
}

export const trackSorting = ({ columnId, isSortedDesc, retailerId }: useTrackSortingProps) => {
  if (previousSorting.columnId !== columnId || previousSorting.isSortedDesc !== isSortedDesc) {
    trackEvent({
      id: TRACKING.sorting.id,
      description: TRACKING.sorting.description,
      data: {
        sortDirection: isSortedDesc ? 'desc' : 'asc',
        column: columnId,
        retailerId,
      },
    })
    previousSorting.columnId = columnId
    previousSorting.isSortedDesc = isSortedDesc
  }
}

export const useTrackPageLoad = () => {
  const retailerId = useRetailerId()
  useEffect(
    () =>
      trackEvent({
        id: TRACKING.pageLoad.id,
        description: TRACKING.pageLoad.description,
        data: { retailerId },
      }),
    [retailerId]
  )
}

interface useTrackDateSelectionProps {
  isWeek: boolean
  startDate: Moment
}

export const useTrackDateSelection = ({ isWeek, startDate }: useTrackDateSelectionProps) => {
  const retailerId = useRetailerId()
  const queryStartDate = momentToQueryStringDate(startDate)
  const queryEndDate = momentToQueryStringDate(isWeek ? getEndOfWeek(startDate) : startDate)
  useEffect(
    () =>
      trackEvent({
        id: TRACKING.dateSelection.id,
        description: TRACKING.dateSelection.description,
        data: {
          startDate: queryStartDate,
          endDate: queryEndDate,
          isWeek,
          retailerId,
        },
      }),
    [isWeek, retailerId, queryStartDate, queryEndDate]
  )
}

interface trackStoreNameClickedProps {
  storeId: string
  retailerId: string
}

export const trackStoreNameClicked = ({ storeId, retailerId }: trackStoreNameClickedProps) => {
  trackEvent({
    id: TRACKING.storeName.id,
    description: TRACKING.storeName.description,
    data: {
      storeId,
      retailerId,
    },
  })
}

interface trackPickupAnalyticsClickedProps {
  retailerId: string
}

export const trackPickupAnalyticsClicked = ({ retailerId }: trackPickupAnalyticsClickedProps) => {
  trackEvent({
    id: TRACKING.pickupAnalytics.id,
    description: TRACKING.pickupAnalytics.description,
    data: {
      retailerId,
    },
  })
}

interface trackExportClickedProps {
  retailerId: string
  storeIds: string[]
  startDate: Moment
  isWeek: boolean
}

export const trackExportClicked = ({
  retailerId,
  storeIds,
  startDate,
  isWeek,
}: trackExportClickedProps) => {
  trackEvent({
    id: TRACKING.export.id,
    description: TRACKING.export.description,
    data: {
      retailerId,
      storeIds,
      startDate: momentToQueryStringDate(startDate),
      endDate: momentToQueryStringDate(isWeek ? getEndOfWeek(startDate) : startDate),
      isWeek,
    },
  })
}

interface trackFiltersChangedOptions {
  retailerId: string
  selectedFilters: FiltersDropdownOption[]
}

export const trackFiltersChanged = ({
  retailerId,
  selectedFilters,
}: trackFiltersChangedOptions) => {
  trackEvent({
    id: TRACKING.filters.id,
    description: TRACKING.filters.description,
    data: {
      retailerId,
      filters: selectedFilters.map(filter => filter.value).toString(),
    },
  })
}
